import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { updateAccount } from '../../api/accounts';
import { formatCurrency } from '../../utils/formatters';
import styles from './ReorderAccountsDialog.module.css';

// Account reorder (Trello gTi9Zrxg item #2) -- persists a manual _displayOrder
// per account, which the backend then uses ahead of its own automatic
// ordering on this page, the MBS report, and the Quality & Completeness
// Report (see backend/assets/lambda/layers/livisibly_common/src/account_order.py).
//
// Accounts are grouped by the SAME canonical grouping the Quality &
// Completeness report uses (Checking / Savings / ... / Other), stamped onto
// each account by the backend (account_grouping.py -> /account/summary as
// account_group + account_group_rank). Reordering is within a group; groups
// stay in their canonical rank order. No drag-and-drop library exists in this
// codebase (confirmed); move-up/down buttons mirror SortLevelsPanel.js.

const OTHER_RANK = 99;
const OTHER_LABEL = 'Other';

const accountLabel = (a) => a?._displayName
  || a?.bank_name
  || `Account ...${a?.account_number_last4 || ''}`;

const accountBalanceLabel = (a) => (a?.latest_balance == null ? '$0.00' : `$${formatCurrency(a.latest_balance)}`);

const moveItem = (arr, index, delta) => {
  const target = index + delta;
  if (index < 0 || index >= arr.length || target < 0 || target >= arr.length) return arr;
  const next = arr.slice();
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

// Bucket accounts into their canonical groups, preserving the incoming
// (backend effective) order within each group, groups sorted by rank.
const buildGroups = (accounts) => {
  const byRank = new Map();
  for (const account of accounts || []) {
    const rank = account.account_group_rank ?? OTHER_RANK;
    const label = account.account_group || OTHER_LABEL;
    if (!byRank.has(rank)) byRank.set(rank, { rank, label, accounts: [] });
    byRank.get(rank).accounts.push(account);
  }
  return Array.from(byRank.values()).sort((a, b) => a.rank - b.rank);
};

export default function ReorderAccountsDialog({ open, accounts, onClose, onSaved }) {
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setGroups(buildGroups(accounts));
      setError(null);
    }
  }, [open, accounts]);

  const moveInGroup = (groupIndex, itemIndex, delta) => {
    setGroups((gs) => gs.map((g, gi) => (
      gi === groupIndex ? { ...g, accounts: moveItem(g.accounts, itemIndex, delta) } : g
    )));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Flatten groups in rank order, each group in the user's within-group
      // order, and re-sequence to a single global 0..N-1 _displayOrder.
      const flat = groups.flatMap((g) => g.accounts);
      const updates = flat
        .map((account, index) => ({ account, index }))
        .filter(({ account, index }) => (account._displayOrder ?? null) !== index);
      await Promise.all(
        updates.map(({ account, index }) => updateAccount(account.id, { _displayOrder: index })),
      );
      if (onSaved) await onSaved();
      onClose();
    } catch (e) {
      setError(e?.message || 'The new order could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reorder accounts</DialogTitle>
      <DialogContent>
        <p className={styles.intro}>
          Reorder accounts within each group. This order applies here, on the Marital
          Balance Sheet, and on the Quality &amp; Completeness Report.
        </p>
        <div className={styles.list}>
          {groups.map((group, groupIndex) => (
            <div key={group.rank} className={styles.group}>
              <div className={styles.groupHeader}>{group.label}</div>
              <ul className={styles.groupList}>
                {group.accounts.map((account, itemIndex) => (
                  <li key={account.id} className={styles.row}>
                    <span className={styles.rowName}>{accountLabel(account)}</span>
                    <span className={styles.rowBalance}>{accountBalanceLabel(account)}</span>
                    <span className={styles.rowActions}>
                      <IconButton
                        size="small"
                        aria-label={`Move ${accountLabel(account)} up`}
                        disabled={saving || itemIndex === 0}
                        onClick={() => moveInGroup(groupIndex, itemIndex, -1)}
                      >
                        <KeyboardArrowUpIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Move ${accountLabel(account)} down`}
                        disabled={saving || itemIndex === group.accounts.length - 1}
                        onClick={() => moveInGroup(groupIndex, itemIndex, 1)}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {error && <div className={styles.error} role="alert">{error}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
